/** @file The user preference for the default tab on the submission page. */
import { exactly } from 'purify-ts/Codec';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import { SiteSettingsStore, UIPrefsStore, UserStore } from '@/store';
/** The user preference for the default tab on the submission page. */
export var SubmissionTabPreset;
(function (SubmissionTabPreset) {
    /** The name of the preference in the database. */
    SubmissionTabPreset.PREF_NAME = 'submission_tab_preset';
    /** Parser for the allowed values of this preference. */
    SubmissionTabPreset.codec = exactly('smart', 'code', 'feedback-overview', 'auto-test', 'peer-feedback');
    /** @returns A promise that resolves once the preferred value has been loaded. */
    SubmissionTabPreset.load = () => UIPrefsStore.loadUIPreference({ preference: SubmissionTabPreset.PREF_NAME });
    /**
     * Update the preferred value for the current user.
     *
     * @param value The new value.
     * @returns A promise that resolves once the new value has been stored.
     */
    SubmissionTabPreset.set = (value) => UIPrefsStore.patchUIPreference({
        preference: SubmissionTabPreset.PREF_NAME,
        value,
    });
    /** @returns The default value of the preference for the logged in user. */
    function defaultValue() {
        const user = UserStore.loggedInUser();
        return user.caseOf({
            Just: u => SiteSettingsStore.getSetting()('DEFAULT_SUBMISSION_PAGE_TAB', u.tenantId),
            Nothing: () => SiteSettingsStore.getGlobalSetting()('DEFAULT_SUBMISSION_PAGE_TAB'),
        });
    }
    /** @returns A maybe of the loading status for tab preferences or the preference itself. */
    function maybeGet() {
        const pref = UIPrefsStore.getUIPref()(SubmissionTabPreset.PREF_NAME, SubmissionTabPreset.codec);
        return expr.match(pref, 'status', {
            'not-loaded': () => Maybe.empty(),
            'not-present': () => Maybe.of(defaultValue()),
            'not-valid': () => Maybe.of(defaultValue()),
            valid: ({ value }) => Maybe.of(value),
        });
    }
    SubmissionTabPreset.maybeGet = maybeGet;
    /** @returns The value for the currently logged in user. */
    function get() {
        return maybeGet().orDefaultLazy(defaultValue);
    }
    SubmissionTabPreset.get = get;
})(SubmissionTabPreset || (SubmissionTabPreset = {}));
